import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContractGenerationService } from '../../../services/contract-generation.service';
import { sanitizeHtml } from 'src/app/shared/utils/safe-html.util';

@Component({
    selector: 'app-contract-template-editor',
    templateUrl: './contract-template-editor.component.html',
    styleUrls: ['./contract-template-editor.component.sass']
})
export class ContractTemplateEditorComponent implements OnInit {
    form: FormGroup;
    loading = false;
    previewMode = false;
    previewHtml: string | null = null;
    availableVariables: { name: string; description: string }[] = [
        { name: '{nome_cliente}', description: 'Nome do cliente' },
        { name: '{documento_cliente}', description: 'Documento do cliente' },
        { name: '{email_cliente}', description: 'E-mail do cliente' },
        { name: '{telefone_cliente}', description: 'Telefone do cliente' },
        { name: '{plano_cliente}', description: 'Nome do plano/sistema' },
        { name: '{valor_mensalidade}', description: 'Valor mensal do plano' },
        { name: '{data_inicio}', description: 'Data de início do contrato' },
        { name: '{endereco_cliente}', description: 'Endereço do cliente' },
        { name: '{cidade_cliente}', description: 'Cidade do cliente' },
        { name: '{estado_cliente}', description: 'Estado do cliente' },
        { name: '{cep_cliente}', description: 'CEP do cliente' },
        { name: '{nome_empresa}', description: 'Nome da empresa emissora' },
        { name: '{cnpj_empresa}', description: 'CNPJ da empresa emissora' }
    ];

    constructor(
        public dialogRef: MatDialogRef<ContractTemplateEditorComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            templateContent: string;
            templateId: string;
            planId: string;
            systemId: string;
            customerId: string;
            isTemporary: boolean;
        },
        private fb: FormBuilder,
        private contractGenerationService: ContractGenerationService,
        private toastService: ToastService,
        private sanitizer: DomSanitizer
    ) {
        this.form = this.fb.group({
            templateContent: [this.data.templateContent || '', Validators.required]
        });
    }

    ngOnInit(): void {
        if (!this.data.templateContent) {
            this.loadTemplateContent();
        } else {
            // Converter formato antigo #var# para novo formato {var}
            this.convertVariableFormats();
        }
    }

    // Função para converter variáveis do formato antigo para o novo
    private convertVariableFormats(): void {
        let content = this.form.get('templateContent')?.value;
        if (content) {
            // Mapeamento dos formatos antigos para novos
            const formatMapping: Record<string, string> = {
                '#nomeCliente#': '{nome_cliente}',
                '#documentoCliente#': '{documento_cliente}',
                '#emailCliente#': '{email_cliente}',
                '#telefoneCliente#': '{telefone_cliente}',
                '#planoCliente#': '{plano_cliente}',
                '#valorMensalidade#': '{valor_mensalidade}',
                '#dataInicio#': '{data_inicio}',
                '#enderecoCliente#': '{endereco_cliente}',
                '#cidadeCliente#': '{cidade_cliente}',
                '#estadoCliente#': '{estado_cliente}',
                '#cepCliente#': '{cep_cliente}',
                '#nomeEmpresa#': '{nome_empresa}',
                '#cnpjEmpresa#': '{cnpj_empresa}'
            };

            // Substituir todas as ocorrências dos formatos antigos
            Object.keys(formatMapping).forEach(oldFormat => {
                content = content.replace(new RegExp(oldFormat, 'g'), formatMapping[oldFormat]);
            });

            // Atualizar o formulário com o conteúdo convertido
            this.form.get('templateContent')?.setValue(content);
        }
    }

    private loadTemplateContent(): void {
        this.loading = true;
        this.contractGenerationService.getTemplateHtmlContent(this.data.templateId).subscribe({
            next: (content: string) => {
                this.form.patchValue({ templateContent: content });
                this.loading = false;
                // Converter formato antigo para novo após carregar
                this.convertVariableFormats();
            },
            error: (error) => {
                this.loading = false;
                this.toastService.showError('Erro ao carregar o conteúdo do template');
                console.error('Erro ao carregar template:', error);
            }
        });
    }

    save(): void {
        if (this.form.invalid) {
            this.toastService.showError('Formulário inválido. Verifique os campos.');
            return;
        }

        this.loading = true;
        const templateContent = this.form.get('templateContent')?.value;

        if (this.data.isTemporary) {
            // Apenas retorna o conteúdo editado para uso temporário
            this.dialogRef.close({
                content: templateContent,
                saved: true
            });
            return;
        }

        // Salva permanentemente no servidor
        this.contractGenerationService.updateTemplateContent(
            this.data.templateId,
            templateContent
        ).subscribe({
            next: () => {
                this.loading = false;
                this.toastService.showSuccess('Template atualizado com sucesso');
                this.dialogRef.close({
                    content: templateContent,
                    saved: true
                });
            },
            error: (error) => {
                this.loading = false;
                this.toastService.showError('Erro ao atualizar o template');
                console.error('Erro ao atualizar template:', error);
            }
        });
    }

    cancel(): void {
        this.dialogRef.close({ saved: false });
    }

    togglePreview(): void {
        this.previewMode = !this.previewMode;

        if (this.previewMode) {
            const templateContent = this.form.get('templateContent')?.value;

            // Formatar o HTML para melhor legibilidade
            const formattedHtml = this.formatHtmlForPreview(templateContent);

            // Criar visualização substituindo variáveis por exemplos
            let previewContent = formattedHtml;
            this.availableVariables.forEach(variable => {
                const placeholderValue = this.getPlaceholderExample(variable.name);
                // Usar expressão regular para substituir todas as ocorrências
                previewContent = previewContent.replace(new RegExp(variable.name.replace(/[{}]/g, '\\$&'), 'g'), placeholderValue);
            });

            this.previewHtml = sanitizeHtml(this.sanitizer, previewContent);
        }
    }

    // Função para formatar HTML para melhor legibilidade
    private formatHtmlForPreview(html: string): string {
        // Verificar se há tags HTML no conteúdo
        if (!html.includes('<')) {
            return html; // Retornar como está se não for HTML
        }

        try {
            // Adicionar estrutura HTML completa se necessário
            if (!html.includes('<!DOCTYPE html>') && !html.includes('<html')) {
                html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 2cm;
            color: #333;
        }
        h1, h2, h3 { color: #00457A; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
    </style>
</head>
<body>
    ${html}
</body>
</html>`;
            }

            return html;
        } catch (error) {
            console.error('Erro ao formatar HTML:', error);
            return html; // Retornar o original em caso de erro
        }
    }

    private getPlaceholderExample(variable: string): string {
        const examples: Record<string, string> = {
            '{nome_cliente}': 'Nome do Cliente Exemplo',
            '{documento_cliente}': '123.456.789-00',
            '{email_cliente}': 'cliente@exemplo.com.br',
            '{telefone_cliente}': '(11) 98765-4321',
            '{plano_cliente}': 'Plano Premium',
            '{valor_mensalidade}': 'R$ 199,90',
            '{data_inicio}': new Date().toLocaleDateString('pt-BR'),
            '{endereco_cliente}': 'Av. Exemplo, 123',
            '{cidade_cliente}': 'São Paulo',
            '{estado_cliente}': 'SP',
            '{cep_cliente}': '01234-567',
            '{nome_empresa}': 'Empresa Exemplo Ltda.',
            '{cnpj_empresa}': '12.345.678/0001-90'
        };

        return examples[variable] || 'Valor de exemplo';
    }

    insertVariable(variable: string): void {
        const templateControl = this.form.get('templateContent');
        if (templateControl) {
            const currentContent = templateControl.value || '';
            const textArea = document.querySelector('textarea[formControlName="templateContent"]') as HTMLTextAreaElement;

            if (textArea) {
                const startPos = textArea.selectionStart;
                const endPos = textArea.selectionEnd;

                const newContent =
                    currentContent.substring(0, startPos) +
                    variable +
                    currentContent.substring(endPos);

                templateControl.setValue(newContent);

                // Reposicionar o cursor após a variável inserida
                setTimeout(() => {
                    textArea.focus();
                    textArea.setSelectionRange(startPos + variable.length, startPos + variable.length);
                }, 0);
            } else {
                // Fallback se não conseguir acesso ao elemento textarea
                templateControl.setValue(currentContent + variable);
            }
        }
    }
} 